// Library methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material/Select";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";

// Services
import { deviceUpdateLensParameter } from "src/services/signalR/api";

// Types
import { ExamLensType } from "src/services/exams/types";

// Utils
import {
  DRY_EYE_FIELDS,
  DRY_EYE_SEVERITY_DEFAULTS,
} from "src/utils/exams/lensSimulationHelper";

// The fields deviceUpdateLensParameter's `data` accepts, minus lensName
// (the parent fills that in) -- derived from the function itself so this
// stays in sync with api.ts.
type LensParameterUpdate = Partial<
  Omit<Parameters<typeof deviceUpdateLensParameter>[2], "lensName">
>;

type DryEyeOptionsType = {
  currentLens: ExamLensType | undefined;
  isCurrentLensActionDisabled: boolean;
  sendLensParameterUpdate: (update: LensParameterUpdate) => Promise<void>;
};

const DryEyeOptions = ({
  currentLens,
  isCurrentLensActionDisabled,
  sendLensParameterUpdate,
}: DryEyeOptionsType) => {
  const { t } = useTranslation();

  const formattedSeverities = useMemo(
    () =>
      Object.keys(DRY_EYE_SEVERITY_DEFAULTS).map((severity) => ({
        displayName: t(severity),
        value: severity,
      })),
    [t]
  );

  const [severity, setSeverity] = useState(
    () =>
      formattedSeverities.find(
        (option) => option.value === currentLens?.severityType
      ) ?? { displayName: "", value: "" }
  );

  const [treated, setTreated] = useState(() => Boolean(currentLens?.treated));

  // Defaults to each field's first option -- matches AddEditDryEyeModal's
  // default when adding a new profile -- unless currentLens already has a
  // valid value for it.
  const [dryEyeEnumValues, setDryEyeEnumValues] = useState<
    Record<string, string>
  >(() =>
    Object.fromEntries(
      DRY_EYE_FIELDS.map((field) => {
        const currentValue = currentLens?.[field.parameterName];
        const isValid = (field.options as readonly string[]).includes(
          currentValue ?? ""
        );
        return [
          field.parameterName,
          isValid ? (currentValue as string) : field.options[0],
        ];
      })
    )
  );

  // Picking a severity resets the fields to that severity's defaults and
  // clears treated, the same as in AddEditDryEyeModal
  const handleSetSeverity = (event: SelectChangeEvent<any>) => {
    const value = formattedSeverities.find(
      (option) => option.displayName === event.target.value
    );
    if (!value) return;

    setSeverity({ ...value });
    const defaultEnumValues = DRY_EYE_SEVERITY_DEFAULTS[value.value];
    setDryEyeEnumValues(defaultEnumValues);
    setTreated(false);
    sendLensParameterUpdate({
      severityType: value.value,
      ...defaultEnumValues,
      treated: false,
    });
  };

  const handleToggleTreated = () => {
    const toggleVal = !treated;
    setTreated(toggleVal);
    sendLensParameterUpdate({ treated: toggleVal });
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(1, 1fr)",
          sm: "repeat(2, 1fr)",
        },
        gap: "8px",
      }}
    >
      <Box display="flex" flexDirection="column" gap="4px">
        <TitleWithTooltip
          title={t("Severity")}
          tooltip={<div>{t("Overall severity of the dry eye condition.")}</div>}
          titleStyle={{ fontSize: "12px" }}
          tooltipStyle={{ width: "16px", height: "16px" }}
        />
        <SelectDropdown
          id="live-dry-eye-severity-dropdown"
          disabled={isCurrentLensActionDisabled}
          dropdownOptions={formattedSeverities}
          value={severity.displayName}
          handleChange={handleSetSeverity}
        />
      </Box>

      {DRY_EYE_FIELDS.map(({ parameterName, title, tooltip, options }) => (
        <Box
          key={parameterName}
          display="flex"
          flexDirection="column"
          gap="4px"
        >
          <TitleWithTooltip
            title={t(title)}
            tooltip={<div>{t(tooltip)}</div>}
            titleStyle={{ fontSize: "12px" }}
            tooltipStyle={{ width: "16px", height: "16px" }}
          />

          <Box display="flex" gap="4px">
            {options.map((option) => (
              <ExamCardButton
                key={`${parameterName}-${option}`}
                label={t(option)}
                isSelected={dryEyeEnumValues[parameterName] === option}
                isDisabled={isCurrentLensActionDisabled}
                updateValue={() => {
                  setDryEyeEnumValues((prev) => ({
                    ...prev,
                    [parameterName]: option,
                  }));
                  sendLensParameterUpdate({ [parameterName]: option });
                }}
              />
            ))}
          </Box>
        </Box>
      ))}

      <Box
        sx={{ gridColumn: "1 / -1" }}
        display="flex"
        alignItems="center"
        gap="8px"
      >
        <CustomCheckbox
          id="live-dry-eye-treated-checkbox"
          checked={treated}
          onClick={handleToggleTreated}
          disabled={isCurrentLensActionDisabled}
          checkedFill="#E2772E"
          checkedStroke="#FFF"
          uncheckedFill="#FFF"
          uncheckedStroke="#5B6D7A"
        />
        <Typography sx={{ fontSize: "12px" }}>{t("Treated")}</Typography>
      </Box>
    </Box>
  );
};

export default DryEyeOptions;
